/**
 * The WEB build's session — the long-lived half in cookies this code cannot
 * read, the access token in memory only.
 *
 * The API brokers the token exchange (`/api/v1/auth/idp/web/*`,
 * identity-architecture §9): it keeps the refresh and ID tokens in HttpOnly,
 * SameSite=Strict cookies path-scoped to those endpoints, and returns only a
 * short-lived access token plus the A3RN session id. Nothing credential-shaped
 * is written to storage, so an XSS finds no refresh token to take
 * (security-audit C1; on web these used to sit in localStorage via
 * AsyncStorage).
 *
 * ⚠️  WEB ONLY. Native is a public OAuth client holding its tokens in the
 *     platform keychain, which is correct per RFC 8252 — every caller branches
 *     on `Platform.OS === 'web'` before reaching this module.
 *
 * Same contract as the admin SPA's `admin/src/auth/session.ts`; keep the two in
 * step.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const WEB = '/api/v1/auth/idp/web';

/** Keys earlier builds stored credentials under; removed on every web load. */
export const LEGACY_TOKEN_KEYS = [
  '@cynosure/access_token',
  '@cynosure/refresh_token',
  '@cynosure/id_token',
] as const;

/**
 * Forget what earlier builds stored. Every key held a credential; they are
 * removed on load so an upgrade leaves nothing behind for an XSS to read, at the
 * cost of one sign-in when this version first ships.
 */
export async function purgeLegacyTokens(): Promise<void> {
  await Promise.all(LEGACY_TOKEN_KEYS.map((k) => AsyncStorage.removeItem(k).catch(() => {})));
}

/** A failure that says nothing about the session — retry, do not sign out. */
export class TransientSessionError extends Error {}

export interface WebSession {
  accessToken: string;
  sessionId: string | null;
}

export interface WebSignIn extends WebSession {
  userId: string;
  created: boolean;
}

async function post(path: string, body?: unknown): Promise<Response> {
  try {
    return await fetch(`${WEB}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new TransientSessionError('Network error');
  }
}

/**
 * Run `fn` while holding a lock shared by every tab of this origin.
 *
 * ⚠️  LOAD-BEARING: ZITADEL ROTATES REFRESH TOKENS, and every tab shares the
 *     one cookie. Two tabs refreshing at once send the same token; the first
 *     rotates it, the second is refused, and the refusal clears the cookie —
 *     signing out every tab, including the one that just succeeded. Reproduced
 *     on the admin SPA against the local cluster before the lock existed.
 *
 * Without the Web Locks API (pre-15.4 Safari) refreshes run unserialized,
 * which is the race above rather than a failure to sign in.
 */
async function acrossTabs<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request('cynosure-web-session-refresh', fn);
}

let signingOut = false;

/**
 * Refuse refreshes from now on in this page. Called at the very START of a
 * sign-out, before local state is cleared — clearing makes mounted queries
 * refetch, 401 and refresh, and one that won the lock first would put a fresh
 * session back. `signOut` sets it too.
 */
export function beginSignOut(): void {
  signingOut = true;
}

/** Exchange the authorization code at the API, which sets the cookies. */
export async function signIn(
  code: string,
  codeVerifier: string,
  redirectUri: string,
): Promise<WebSignIn> {
  const response = await post('/token', {
    code,
    code_verifier: codeVerifier,
    redirect_uri: redirectUri,
  });
  if (!response.ok) {
    const detail = ((await response.json().catch(() => null)) as { detail?: unknown } | null)
      ?.detail;
    throw new Error(typeof detail === 'string' ? detail : 'Sign-in could not be completed');
  }
  const body = (await response.json()) as {
    access_token: string;
    session_id: string | null;
    user_id: string;
    created: boolean;
  };
  // A fresh sign-in in this page re-arms refresh after an earlier sign-out.
  signingOut = false;
  return {
    accessToken: body.access_token,
    sessionId: body.session_id,
    userId: body.user_id,
    created: body.created,
  };
}

/**
 * A fresh access token from the cookie, or null when there is no session.
 *
 * ⚠️  null means SIGNED OUT, and only a 401 produces it — a 5xx or a network
 *     failure throws TransientSessionError, so a blip never logs the user out.
 */
export async function refresh(): Promise<WebSession | null> {
  return acrossTabs(async () => {
    // Once sign-out has begun nothing may mint a session again — see signOut.
    if (signingOut) return null;
    const response = await post('/refresh');
    if (response.status === 401) return null;
    if (!response.ok) throw new TransientSessionError(`Refresh failed (${response.status})`);
    const body = (await response.json()) as { access_token: string; session_id: string | null };
    return { accessToken: body.access_token, sessionId: body.session_id };
  });
}

/**
 * Clear the cookies and revoke the refresh token at the API, and return where
 * the browser should go to end the IdP session (null when there is nowhere).
 *
 * ⚠️  UNDER THE REFRESH LOCK, and that is what makes sign-out stick. Clearing
 *     the in-memory token makes requests still in flight 401 and refresh;
 *     unserialized, such a refresh could land AFTER the logout and set a
 *     brand-new cookie — a working session behind a "Sign out" click.
 *     Reproduced on the admin SPA. Holding the lock, logout waits out any
 *     refresh already running and revokes whatever token is newest; refresh()
 *     refuses from here on.
 */
export async function signOut(): Promise<string | null> {
  signingOut = true;
  try {
    const response = await acrossTabs(() => post('/logout'));
    if (!response.ok) return null;
    return ((await response.json()) as { end_session_url: string | null }).end_session_url;
  } catch {
    // Signed out locally regardless; there is just no IdP hop to make.
    return null;
  }
}

// iOS Safari's back-forward cache can restore this page — module state and
// all — when the user taps Back from the IdP. A restored page starts over.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) signingOut = false;
  });
}
